import { HttpClient, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environments';

const baseUrl = environment.baseUrl;

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const http = inject(HttpClient);
  const platformId = inject(PLATFORM_ID);

  // ✅ SSR → Proceed without token (localStorage not available)
  if (!isPlatformBrowser(platformId)) {
    return next(req);
  }

  // ✅ Skip auth handling for public auth endpoints
  const isPublicAuthEndpoint =
    req.url.includes('/signin/member-sign') || req.url.includes('/signin/member-verify-sign-in-otp');

  // ✅ Skip token generation API itself and public auth APIs
  if (req.url.includes('/user/get-access-token') || isPublicAuthEndpoint) {
    return next(req);
  }

  const tokenRaw = localStorage.getItem('userToken');
  let token: string | null = null;

  // ✅ Parse token safely
  if (tokenRaw && tokenRaw !== 'undefined') {
    try {
      let parsed: any = JSON.parse(tokenRaw);

      // Handle double-stringified JSON
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      token = parsed?.token ?? null;
      //   console.log('Parsed token from localStorage:', token);
    } catch {
      // Fallback if parsing fails
      token = tokenRaw;
      ///   console.log('Failed to parse token from localStorage, using raw value.', token);
    }
  }

  if (token) {
    // ✅ Attach token and proceed
    const reqWithHeader = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return next(reqWithHeader);
  }

  //  No token → Fetch it from API
  const url = `${baseUrl}/user/get-access-token`;

  return http.get(url, { responseType: 'text' }).pipe(
    tap((newToken) => {
      if (newToken) {
        //  Remove wrapping quotes if present
        const cleanedToken = newToken.replace(/^"|"$/g, '');

        // ✅ Store clean token
        localStorage.setItem('userToken', cleanedToken);
      }
    }),
    switchMap((newToken) => {
      const cleanedToken = newToken.replace(/^"|"$/g, '');

      //console.log('Fetched new token from API:', cleanedToken);

      const reqWithHeader = req.clone({
        setHeaders: {
          Authorization: `Bearer ${cleanedToken}`,
        },
      });
      return next(reqWithHeader);
    }),
  );
};
